import { ContentToken } from "@/lib/content/types";
import { Question } from "./question-schema";
import { TopicTests } from "./test-schema";

interface Subject {
  documentId: string;
  Name: string;
  RelatedTopics: Topic[];
}

interface Topic {
  documentId: string;
  TopicNumber: number;
  TopicTitle: string;
  IsHigherLevel: boolean;
  TopicDescription: string;
  RelatedSubTopics: SubTopic[];
  RelatedTopicTests?: TopicTests;
}

interface SubTopic {
  documentId: string;
  SubTopicNumber: number;
  SubTopicTitle: string;
  OfficialTag?: string;
  SubTopicDescription: string;
  IsHigherLevel: boolean;
  RelatedSyllabusPoints: SyllabusPoint[];
  RelatedSubThemes: SubTopicTheme[];
  RelatedQuestions?: Question[];
}

interface SubTopicTheme {
  documentId: string;
  SubThemeNumber: number;
  SubThemeTitle: string;
  SubThemeDescription: string;
}

interface SyllabusPoint {
  documentId: string;
  Point: ContentToken[];
  TypeOfPoint: "Understanding" | "Application";
  SyllabusPointNumber: number;
}

export type TrackedResources =
  | "videos"
  | "notes"
  | "definitions"
  | "flashcards"
  | "questions"
  | "cram sheets"
  | "topic tests"
  | "test taker"
  | "mock exams";

export type Resources =
  | "videos"
  | "notes"
  | "definitions"
  | "flashcards"
  | "questions"
  | "cram sheets"
  | "topic tests"
  | "individual topic test"
  | "test taker"
  | "mock exams"
  | "test builder"
  | "command terms"
  | "exam breakdown"
  | "ia examples"
  | "ia criteria"
  | "syllabus"
  | "exam tests";

export enum Tools {
  ADMIN_TOOLS = "admin tools",
}

interface CurrentTopicInfo {
  currentSubject: Subject;
  currentTopic: Topic;
  subTopicDocumentIds: string[];
  subTopicThemeTitles:
    | {
        documentId: string;
        title: string;
      }[]
    | [];
}

interface TopicSyllabusPointMap {
  total: number;
  subTopics: {
    [subTopicDocumentId: SubTopic["documentId"]]: SyllabusPoint["documentId"][];
  };
}

interface SubjectGraphLabelMap {
  [subjectDocumentId: string]: Array<{
    documentId: string;
    number: number;
    modNumber: number;
    title: string;
    subTopics: SubTopic["documentId"][];
  }>;
}

// site total schema
interface SubjectWithResourcesSchema {
  documentId: string;
  Name: string;
  RelatedTopics: RelatedTopicWithAttributes[];
}

interface RelatedTopicWithAttributes extends Attributes {
  documentId: string;
}

interface Attributes extends StrippedAttributes {
  SubTopicTitle: string;
}

interface StrippedAttributes {
  RelatedVideos: EmptyResource[] | [];
  RelatedNotes: EmptyResource[] | [];
  RelatedFlashCards: EmptyResource[] | [];
  RelatedQuestions: QuestionResource[] | [];
  RelatedCramSheets: EmptyResource[] | [];
  RelatedDefinitions: EmptyResource[] | [];
}

interface QuestionResource extends EmptyResource {
  QuestionStyle: string;
}

interface EmptyResource {
  documentId: string;
}

export interface AverageSubjectGradeBoundariesSchema {
  [subjectDocumentId: string]: {
    SL: GradeBoundary;
    HL: GradeBoundary;
  };
}

export type GradeBoundary = {
  grade1: number;
  grade2: number;
  grade3: number;
  grade4: number;
  grade5: number;
  grade6: number;
  grade7: number;
};
